<template>
  <div class="w-full bg-primary">
    <table class="table w-full border-collapse table-fixed">
      <TableHeader
        :visibleColumns="visibleColumns"
        :isSelectable="isSelectable"
        :stickyHeader="stickyHeader"
        :selectAllValue="selectAllValue"
        :sortBy="sortBy"
        :orderBy="orderBy"
        @update:sort="handleUpdateSort"
        @update:selection="handleUpdateSelection(data, $event)"
      />
      <TableBody
        :data="data"
        :isLoading="isLoading"
        :visibleColumns="visibleColumns"
        :isSelectable="isSelectable"
        :selectedRows="selectedRows"
        :rowHeight="rowHeight"
        :hoveredRow="hoveredRow"
        :type="type"
        @clicked:row="emit('clicked:row', $event)"
        @hover:row="emit('hover:row', $event)"
        @update:selection="handleUpdateSelection(data, $event)"
      />
    </table>
  </div>
  <Teleport
    v-if="!isLoading && showManageColumnsIcon"
    to="#column-picker"
    defer
  >
    <ColumnPicker
      :selectedColumns="showColumns.map((col) => col)"
      :columns="columns"
      v-model:open="isColumnPickerOpen"
      @update:selection="emit('update:column-preferences', $event)"
    >
      <Tooltip
        :title="$t('components.tooltips.select_column')"
        side="bottom"
        disableHoverableContent
      >
        <Button
          icon
          size="xs"
          variant="tertiary"
          type="gray"
          :class="{
            'bg-utility-brand-50_alt fg-brand-primary_alt': isColumnPickerOpen,
          }"
          data-id="header.column-selector"
          :disabled="loading"
        >
          <template #start>
            <Icon name="filter" />
          </template>
        </Button>
      </Tooltip>
    </ColumnPicker>
  </Teleport>
</template>

<script setup>
// Import Vue utilities
import { computed, onMounted, ref, useSlots } from "vue";

// Import utilties
import { storeToRefs } from "pinia";
import { waitForElement } from "@/Utils/wait";

// Import Components
import { Icon } from "@/SharedComponents/Icon";
import { Button } from "@/SharedComponents/Buttons";
import {
  TableHeader,
  TableBody,
  ColumnPicker,
} from "@/SharedComponents/DataGrid";
import { Tooltip } from "@/SharedComponents/Tooltips";

// Import Composables
import { useViewControls } from "@/Composables/useViewControls";
import { useSelectedRows } from "@/Composables/useSelectedRows";

// View Controls: Global
const viewControlsStore = useViewControls("viewControls");
const { loading } = storeToRefs(viewControlsStore);

// Define Props
const props = defineProps({
  data: {
    type: Array,
    required: true,
  },
  sortBy: {
    type: String,
    default: "",
  },
  orderBy: {
    type: String,
    default: "asc",
  },
  isLoading: {
    type: Boolean,
    required: false,
  },
  isSelectable: {
    type: Boolean,
    required: false,
  },
  rowHeight: {
    type: String,
    required: false,
    default: "60",
  },
  stickyHeader: {
    type: Boolean,
    default: true,
  },
  showColumns: {
    type: [Array],
    default: () => [],
  },
  selectedColumns: {
    type: [Array],
    default: () => [],
  },
  hasColumnPicker: {
    type: Boolean,
    default: false,
  },
  hoveredRow: {
    type: String,
    default: null,
  },
  type: {
    type: String,
    required: false,
  },
});

// Emits
const emit = defineEmits([
  "update:sort",
  "update:column-preferences",
  "clicked:row",
  "hover:row",
]);

// Slots
const slots = useSlots();

// Refs
const isColumnPickerOpen = ref(false);
const showManageColumnsIcon = ref(false);

const selectedRowsStore = useSelectedRows(props.type);
const { selectedRows, selectAllValue } = storeToRefs(selectedRowsStore);
const { handleUpdateSelection, handleClearSelection } = selectedRowsStore;

// Computed Props
const columns = computed(() => {
  const localColumns =
    slots?.default?.().map(({ props, children }) => ({
      props,
      cell: children?.cell ?? null,
    })) || [];

  if (props.showColumns?.length) {
    const indexMap = new Map(
      props.showColumns.map((field, index) => [field, index])
    );
    localColumns.sort(
      (a, b) =>
        (indexMap.get(a.props.field) ?? Infinity) -
        (indexMap.get(b.props.field) ?? Infinity)
    );
  }

  return localColumns;
});

const selectedColumns = computed(() => {
  const localShowColumns = props.showColumns.filter((column) =>
    columns.value.some((col) => col.props.field === column)
  );

  return columns.value.filter((column) =>
    localShowColumns.length > 0
      ? localShowColumns.includes(column.props.field)
      : true
  );
});

const visibleColumns = computed(() => {
  if (props.hasColumnPicker) {
    return props.showColumns?.length === 0
      ? []
      : columns.value.filter((column) =>
          selectedColumns.value.includes(column)
        );
  }

  return columns.value;
});

// Functions
const handleUpdateSort = (field, order) => {
  emit("update:sort", field, order);
};

onMounted(() => {
  if (!props.hasColumnPicker) {
    return;
  }

  waitForElement("#column-picker", () => {
    showManageColumnsIcon.value = true;
  });
});

defineExpose({
  handleClearSelection,
});
</script>
